// Swaps — manager view of all shift-swap requests. Redesigned as a premium
// card-based layout with clear "FROM → TO" visualization, avatar chips,
// status filters, and swipe-to-approve on mobile.
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, X, Clock, Repeat, User as UserIcon } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader, Chip, statusVariant } from "@/components/ui-bits";
import PaperPlaneEmpty from "@/components/PaperPlaneEmpty";
import SwipeAction from "@/components/SwipeAction";
import { avatarBg } from "@/lib/roleColors";
import { toast } from "sonner";

function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((n) => n[0]?.toUpperCase()).join("");
}

// Relative "aangevraagd X dagen geleden" — Dutch first, English fallback.
function timeAgo(iso, t) {
  if (!iso) return "";
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  if (Number.isNaN(ms) || ms < 0) return "";
  const min = Math.floor(ms / 60000);
  const hrs = Math.floor(min / 60);
  const days = Math.floor(hrs / 24);
  const weeks = Math.floor(days / 7);
  if (min < 1) return t("time_just_now");
  if (min < 60) return t("time_minutes_ago").replace("{n}", min);
  if (hrs < 24) return t("time_hours_ago").replace("{n}", hrs);
  if (days === 1) return t("time_yesterday");
  if (days < 7) return t("time_days_ago").replace("{n}", days);
  if (weeks === 1) return t("time_one_week_ago");
  if (days < 30) return t("time_weeks_ago").replace("{n}", weeks);
  const months = Math.floor(days / 30);
  return t("time_months_ago").replace("{n}", months);
}

const FILTERS = [
  { key: "actionable", labelKey: "swap_filter_actionable" },
  { key: "all", labelKey: "swap_filter_all" },
  { key: "pending", labelKey: "swap_filter_pending" },
  { key: "accepted", labelKey: "swap_filter_accepted" },
  { key: "approved", labelKey: "swap_filter_approved" },
  { key: "rejected", labelKey: "swap_filter_rejected" },
];

export default function Swaps() {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("actionable");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/swap-requests");
      setList(data);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const decide = async (id, action) => {
    try {
      await api.post(`/swap-requests/${id}/action`, { action });
      toast.success(t("saved"));
      load();
    } catch (e) {
      toast.error(t("error"));
    }
  };

  // Reformat "YYYY-MM-DD HH:MM–HH:MM" → "23 jul · 09:00–17:00"
  const fmt = (raw) => {
    if (!raw) return "";
    const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})\s+(.+)$/);
    if (!m) return raw;
    try {
      const d = new Date(`${m[1]}-${m[2]}-${m[3]}T12:00:00`);
      return `${d.toLocaleDateString("nl", { day: "numeric", month: "short" })} · ${m[4]}`;
    } catch { return raw; }
  };

  const filtered = useMemo(() => {
    if (filter === "all") return list;
    if (filter === "actionable") return list.filter((s) => s.status === "accepted" || s.status === "pending");
    return list.filter((s) => s.status === filter);
  }, [list, filter]);

  const counts = useMemo(() => ({
    actionable: list.filter((s) => s.status === "accepted" || s.status === "pending").length,
    pending: list.filter((s) => s.status === "pending").length,
    accepted: list.filter((s) => s.status === "accepted").length,
    approved: list.filter((s) => s.status === "approved").length,
    rejected: list.filter((s) => s.status === "rejected").length,
  }), [list]);

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="page-swaps">
      <PageHeader
        eyebrow={t("navsec_team")}
        title={t("swap_requests")}
        subtitle={t("swap_subtitle") || "Beoordeel dienstwissels van je team op één plek"}
      />

      {/* Filter tabs */}
      <div className="mt-4 -mx-1 px-1 overflow-x-auto no-scrollbar">
        <div className="inline-flex items-center gap-1 p-1 rounded-2xl bg-[hsl(var(--muted))]" data-testid="swap-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`swap-filter-${f.key}`}
              className={`h-9 px-3.5 rounded-xl text-[12.5px] font-semibold whitespace-nowrap transition-all inline-flex items-center gap-1.5 ${
                filter === f.key
                  ? "bg-[hsl(var(--surface))] text-[hsl(var(--text-strong))] shadow-[var(--shadow-sm)]"
                  : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"
              }`}
            >
              {t(f.labelKey) || f.key}
              {counts[f.key] > 0 && (
                <span className={`tabular-nums text-[10.5px] px-1.5 py-0.5 rounded-full font-bold ${
                  filter === f.key ? "bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]" : "bg-[hsl(var(--surface))] text-[hsl(var(--text-muted))]"
                }`}>{counts[f.key]}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="mt-5 flex flex-col gap-2.5">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="card p-4 flex items-center gap-3">
              <div className="skeleton w-10 h-10 rounded-xl" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-3 rounded w-1/2" />
                <div className="skeleton h-2.5 rounded w-1/3" />
              </div>
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-6" data-testid="swap-empty">
            <PaperPlaneEmpty size={140} type="sent" />
            <h3 className="mt-5 font-display text-[17px] font-bold text-[hsl(var(--text-strong))] text-center">
              {t("swap_empty_title") || "Geen ruilverzoeken"}
            </h3>
            <p className="mt-1.5 text-[12.5px] text-[hsl(var(--text-muted))] text-center max-w-[320px]">
              {filter === "actionable"
                ? (t("swap_empty_actionable") || "Er is niks dat op jouw goedkeuring wacht.")
                : (t("swap_empty_hint") || "Zodra je team diensten wil ruilen, verschijnen ze hier.")}
            </p>
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {filtered.map((s) => (
              <SwapCard key={s._id} s={s} t={t} fmt={fmt} decide={decide} timeAgo={timeAgo} />
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}

function SwapCard({ s, t, fmt, decide, timeAgo }) {
  const canApprove = s.status === "accepted";
  const canReject = s.status === "accepted" || s.status === "pending";
  const isFinal = ["approved", "rejected"].includes(s.status);
  const requested = timeAgo(s.created_at, t);
  const isUrgent = s.status === "accepted" && s.created_at &&
    (Date.now() - new Date(s.created_at).getTime()) > 2 * 86400000; // > 2 days waiting

  const cardInner = (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.98 }}
      transition={{ duration: 0.2 }}
      className={`card p-4 md:p-5 ${isFinal ? "opacity-75" : ""}`}
      data-testid={`swap-${s._id}`}
    >
      {/* Top row: FROM → TO */}
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0 flex items-center gap-3">
          {/* FROM avatar */}
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              className="w-10 h-10 rounded-xl grid place-items-center text-white text-[13px] font-bold shrink-0"
              style={{ background: avatarBg(s.from_employee_name, s.from_employee_role) }}
            >
              {initials(s.from_employee_name)}
            </div>
            <div className="min-w-0">
              <div className="text-[10.5px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">{t("from")}</div>
              <div className="text-[13.5px] font-semibold text-[hsl(var(--text-strong))] truncate">{s.from_employee_name}</div>
            </div>
          </div>

          <ArrowRight size={16} className="text-[hsl(var(--text-muted))] shrink-0 mx-1" />

          {/* TO avatar or open badge */}
          <div className="flex items-center gap-2.5 min-w-0">
            {s.target_employee_name ? (
              <>
                <div
                  className="w-10 h-10 rounded-xl grid place-items-center text-white text-[13px] font-bold shrink-0"
                  style={{ background: avatarBg(s.target_employee_name, s.target_employee_role) }}
                >
                  {initials(s.target_employee_name)}
                </div>
                <div className="min-w-0">
                  <div className="text-[10.5px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">{t("to")}</div>
                  <div className="text-[13.5px] font-semibold text-[hsl(var(--text-strong))] truncate">{s.target_employee_name}</div>
                </div>
              </>
            ) : (
              <>
                <div className="w-10 h-10 rounded-xl grid place-items-center bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))] border-2 border-dashed border-[hsl(var(--text-soft))] shrink-0">
                  <UserIcon size={16} strokeWidth={1.75} />
                </div>
                <div>
                  <div className="text-[10.5px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">{t("to")}</div>
                  <div className="text-[12.5px] font-semibold text-[hsl(var(--text-muted))] italic">
                    {t("open_to_anyone") || "Iedereen mag"}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <Chip variant={statusVariant(s.status)}>{t(s.status)}</Chip>
      </div>

      {/* Shift meta line */}
      <div className="mt-3.5 flex items-center gap-2 flex-wrap text-[12px]">
        <span className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] font-semibold tabular-nums">
          <Clock size={11} strokeWidth={2.4} />
          {fmt(s.shift_summary)}
        </span>
        {requested && (
          <span
            className={`inline-flex items-center gap-1 px-2 h-7 rounded-full text-[11px] font-medium tabular-nums ${
              isUrgent
                ? "bg-[hsl(45_92%_92%)] text-[hsl(30_92%_38%)]"
                : "bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))]"
            }`}
            data-testid={`swap-requested-${s._id}`}
            title={s.created_at ? new Date(s.created_at).toLocaleString("nl") : ""}
          >
            <Clock size={10} strokeWidth={2.4} />
            {t("swap_requested_prefix").replace("{ago}", requested)}
          </span>
        )}
        {s.message && (
          <span className="text-[hsl(var(--text-muted))] italic truncate">"{s.message}"</span>
        )}
      </div>

      {/* Actions */}
      {!isFinal && (
        <div className="mt-4 pt-3 border-t hairline flex items-center justify-between gap-2">
          <div className="text-[11.5px] text-[hsl(var(--text-muted))]">
            {s.status === "pending"
              ? (t("swap_awaiting_target") || "Wacht op reactie van collega")
              : (t("swap_ready_for_approval") || "Beide werknemers akkoord — jouw goedkeuring nodig")}
          </div>
          <div className="flex items-center gap-2">
            {canReject && (
              <button
                onClick={() => decide(s._id, "reject")}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border hairline text-[12.5px] font-semibold text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-soft))] transition-colors"
                data-testid={`reject-swap-${s._id}`}
              >
                <X size={13} strokeWidth={2.5} />
                {t("reject") || "Afwijzen"}
              </button>
            )}
            {canApprove && (
              <button
                onClick={() => decide(s._id, "approve")}
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-[hsl(var(--success))] text-white text-[12.5px] font-bold hover:brightness-105 transition-all shadow-[var(--shadow-sm)]"
                data-testid={`approve-swap-${s._id}`}
              >
                <Check size={13} strokeWidth={2.6} />
                {t("approve")}
              </button>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );

  // Wrap actionable swaps in swipe gesture (mobile)
  if (canApprove) {
    return (
      <SwipeAction
        testid={`swipe-swap-${s._id}`}
        onApprove={() => decide(s._id, "approve")}
        onReject={() => decide(s._id, "reject")}
      >
        {cardInner}
      </SwipeAction>
    );
  }
  return cardInner;
}
